import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getAllBrands, getModelsByBrand, getOfferTypes, getFuelTypes, getUserVehicles } from '../services/vehicleCrudService'
import './AddVehicle.css'

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api'

const EditVehicle = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { vehicleId } = useParams()

  const [formData, setFormData] = useState({
    brandId: '',
    modelId: '',
    year: new Date().getFullYear(),
    price: '',
    description: '',
    kilometers: '',
    power: '',
    offerTypeId: '',
    fuelTypeId: '',
    images: [''],
  })

  const [brands, setBrands] = useState([])
  const [models, setModels] = useState([])
  const [offerTypes, setOfferTypes] = useState([])
  const [fuelTypes, setFuelTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Cargar datos iniciales y vehículo
  useEffect(() => {
    if (!user) {
      navigate('/')
      return
    }

    loadInitialData()
    loadVehicleData()
  }, [user, navigate, vehicleId])

  const loadInitialData = async () => {
    try {
      const [brandsData, offerTypesData, fuelTypesData] = await Promise.all([getAllBrands(), getOfferTypes(), getFuelTypes()])

      setBrands(brandsData)
      setOfferTypes(offerTypesData)
      setFuelTypes(fuelTypesData)
    } catch (err) {
      console.error('Error cargando datos:', err)
      setError('Error cargando los datos del formulario')
    }
  }

  const loadVehicleData = async () => {
    try {
      setLoading(true)

      // Usar getUserVehicles que ya formatea las imágenes correctamente
      const vehicles = await getUserVehicles(user.id)
      const vehicle = vehicles.find(v => v['vehicle_id(PK)'] === parseInt(vehicleId))

      if (!vehicle) {
        throw new Error('Vehículo no encontrado')
      }

      // Cargar offer types y fuel types si aún no están cargados
      let currentOfferTypes = offerTypes
      let currentFuelTypes = fuelTypes

      if (currentOfferTypes.length === 0) {
        currentOfferTypes = await getOfferTypes()
        setOfferTypes(currentOfferTypes)
      }

      if (currentFuelTypes.length === 0) {
        currentFuelTypes = await getFuelTypes()
        setFuelTypes(currentFuelTypes)
      }

      // Primero obtener la marca_id del modelo
      const modelResponse = await fetch(`${API_URL}/brands`)
      const brandsData = await modelResponse.json()

      // Buscar qué marca tiene este modelo
      let foundBrandId = null
      let foundModelId = null
      for (const brand of brandsData) {
        const modelsResponse = await fetch(`${API_URL}/brands/${brand['marca_id(PK)']}/models`)
        const modelsData = await modelsResponse.json()
        const foundModel = modelsData.find(m => m.nom === vehicle.model_name)
        if (foundModel) {
          foundBrandId = brand['marca_id(PK)']
          foundModelId = foundModel['model_id(PK)']
          setModels(modelsData)
          break
        }
      }

      // Buscar IDs de offer type y fuel type
      const offerType = currentOfferTypes.find(ot => ot.nom === vehicle.offer_type_name)
      const fuelType = currentFuelTypes.find(ft => ft.nom === vehicle.fuel_type_name)

      console.log('🖼️ Imágenes del vehículo:', vehicle.images)

      // Las imágenes ya vienen formateadas como strings desde getUserVehicles
      const imageUrls = Array.isArray(vehicle.images) && vehicle.images.length > 0 ? vehicle.images : []

      console.log('🖼️ URLs procesadas:', imageUrls)

      setFormData({
        brandId: foundBrandId || '',
        modelId: foundModelId || '',
        year: vehicle.any,
        price: vehicle.preu,
        description: vehicle.descripcio || '',
        kilometers: vehicle.kilometratge || '',
        power: vehicle.potencia || '',
        offerTypeId: offerType?.['tipus_oferta_id(PK)'] || '',
        fuelTypeId: fuelType?.['tipus_combustible_id(PK)'] || '',
        images: imageUrls.length > 0 ? imageUrls : [''],
      })
    } catch (err) {
      console.error('Error cargando vehículo:', err)
      setError('Error al cargar el vehículo')
    } finally {
      setLoading(false)
    }
  }

  const handleBrandChange = async brandId => {
    setFormData({ ...formData, brandId, modelId: '' })
    setModels([])

    if (brandId) {
      try {
        const modelsData = await getModelsByBrand(brandId)
        setModels(modelsData)
      } catch (err) {
        console.error('Error cargando modelos:', err)
      }
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images]
    newImages[index] = value
    setFormData({ ...formData, images: newImages })

    if (error && error.includes('URL duplicada')) {
      setError('')
    }
  }

  const addImageField = () => {
    const hasEmpty = formData.images.some(img => img.trim() === '')
    if (!hasEmpty) {
      setFormData({ ...formData, images: [...formData.images, ''] })
    }
  }

  const removeImageField = index => {
    const newImages = formData.images.filter((_, i) => i !== index)
    if (newImages.length === 0) {
      newImages.push('')
    }
    setFormData({ ...formData, images: newImages })
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setSaving(true)
    setError('')

    try {
      if (!formData.brandId || !formData.modelId || !formData.offerTypeId) {
        throw new Error('Por favor, completa todos los campos obligatorios')
      }

      const validImages = formData.images.map(img => img.trim()).filter(img => img !== '')

      const uniqueImages = [...new Set(validImages)]
      if (uniqueImages.length < validImages.length) {
        throw new Error('⚠️ Tienes URLs de imágenes duplicadas')
      }

      const invalidUrls = uniqueImages.filter(url => {
        try {
          new URL(url)
          return false
        } catch {
          return true
        }
      })

      if (invalidUrls.length > 0) {
        throw new Error('⚠️ Algunas URLs no son válidas')
      }

      const vehicleData = {
        modelId: formData.modelId,
        year: parseInt(formData.year),
        price: parseFloat(formData.price),
        description: formData.description,
        kilometers: parseInt(formData.kilometers),
        power: parseInt(formData.power),
        offerTypeId: formData.offerTypeId,
        fuelTypeId: formData.fuelTypeId,
        images: uniqueImages,
      }

      // Llamar al endpoint de actualización
      const response = await fetch(`${API_URL}/vehicles/${vehicleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...vehicleData,
          userId: user.id,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al actualizar vehículo')
      }

      alert('✅ Vehículo actualizado correctamente')
      navigate('/mis-vehiculos')
    } catch (err) {
      console.error('Error actualizando vehículo:', err)
      setError(err.message || 'Error al actualizar el vehículo')
    } finally {
      setSaving(false)
    }
  }

  if (!user) {
    return null
  }

  if (loading) {
    return (
      <div className='add-vehicle-page'>
        <div className='add-vehicle-container'>
          <div className='loading-message'>Cargando vehículo...</div>
        </div>
      </div>
    )
  }

  return (
    <div className='add-vehicle-page'>
      <div className='add-vehicle-container'>
        <div className='add-vehicle-header'>
          <button className='back-button' onClick={() => navigate('/mis-vehiculos')}>
            ← Volver
          </button>
          <h1>Editar Vehículo</h1>
        </div>

        {error && <div className='error-message'>{error}</div>}

        <form onSubmit={handleSubmit} className='vehicle-form'>
          {/* Marca y Modelo */}
          <div className='form-row'>
            <div className='form-group'>
              <label>
                Marca <span className='required'>*</span>
              </label>
              <select name='brandId' value={formData.brandId} onChange={e => handleBrandChange(e.target.value)} required>
                <option value=''>Selecciona una marca</option>
                {brands.map(brand => (
                  <option key={brand['marca_id(PK)']} value={brand['marca_id(PK)']}>
                    {brand.nom}
                  </option>
                ))}
              </select>
            </div>

            <div className='form-group'>
              <label>
                Modelo <span className='required'>*</span>
              </label>
              <select name='modelId' value={formData.modelId} onChange={handleChange} required disabled={!formData.brandId}>
                <option value=''>Selecciona un modelo</option>
                {models.map(model => (
                  <option key={model['model_id(PK)']} value={model['model_id(PK)']}>
                    {model.nom}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Año, Precio, Kilómetros */}
          <div className='form-row'>
            <div className='form-group'>
              <label>Año</label>
              <input type='number' name='year' value={formData.year} onChange={handleChange} min='1900' max={new Date().getFullYear() + 1} />
            </div>

            <div className='form-group'>
              <label>Precio (€)</label>
              <input type='number' name='price' value={formData.price} onChange={handleChange} min='0' step='0.01' />
            </div>

            <div className='form-group'>
              <label>Kilómetros</label>
              <input type='number' name='kilometers' value={formData.kilometers} onChange={handleChange} min='0' />
            </div>
          </div>

          {/* Potencia, Tipo de oferta, Combustible */}
          <div className='form-row'>
            <div className='form-group'>
              <label>Potencia (CV)</label>
              <input type='number' name='power' value={formData.power} onChange={handleChange} min='0' />
            </div>

            <div className='form-group'>
              <label>
                Tipo de oferta <span className='required'>*</span>
              </label>
              <select name='offerTypeId' value={formData.offerTypeId} onChange={handleChange} required>
                <option value=''>Selecciona tipo</option>
                {offerTypes.map(type => (
                  <option key={type['tipus_oferta_id(PK)']} value={type['tipus_oferta_id(PK)']}>
                    {type.nom}
                  </option>
                ))}
              </select>
            </div>

            <div className='form-group'>
              <label>Tipo de combustible</label>
              <select name='fuelTypeId' value={formData.fuelTypeId} onChange={handleChange}>
                <option value=''>Selecciona combustible</option>
                {fuelTypes.map(type => (
                  <option key={type['tipus_combustible_id(PK)']} value={type['tipus_combustible_id(PK)']}>
                    {type.nom}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Descripción */}
          <div className='form-group'>
            <label>Descripción</label>
            <textarea name='description' value={formData.description} onChange={handleChange} rows='4' placeholder='Describe el vehículo...'></textarea>
          </div>

          {/* URLs de Imágenes */}
          <div className='form-group'>
            <label>URLs de Imágenes</label>

            {formData.images.map((imageUrl, index) => (
              <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input type='url' value={imageUrl} onChange={e => handleImageChange(index, e.target.value)} placeholder='https://ejemplo.com/imagen.jpg' style={{ flex: 1 }} />
                {formData.images.length > 1 && (
                  <button type='button' onClick={() => removeImageField(index)} className='remove-image-btn'>
                    ✕
                  </button>
                )}
              </div>
            ))}

            <button type='button' onClick={addImageField} className='add-image-btn'>
              + Añadir otra imagen
            </button>

            {/* Preview de imágenes */}
            {formData.images.filter(img => typeof img === 'string' && img.trim() !== '').length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ color: '#61dafb' }}>Preview de imágenes ({formData.images.filter(img => typeof img === 'string' && img.trim() !== '').length})</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                  {formData.images
                    .filter(img => typeof img === 'string' && img.trim() !== '')
                    .map((imageUrl, index) => (
                      <div key={index} style={{ position: 'relative', paddingTop: '100%', background: '#282c34', borderRadius: '8px', overflow: 'hidden' }}>
                        <img
                          src={imageUrl}
                          alt={`Preview ${index + 1}`}
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={e => {
                            e.target.style.display = 'none'
                            const errorDiv = e.target.nextElementSibling
                            if (errorDiv) errorDiv.style.display = 'flex'
                          }}
                        />
                        <div
                          style={{
                            display: 'none',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            background: 'rgba(255, 107, 107, 0.2)',
                            color: '#ff6b6b',
                            fontSize: '12px',
                            textAlign: 'center',
                            padding: '10px',
                          }}
                        >
                          <span style={{ fontSize: '24px' }}>⚠️</span>
                          <span style={{ marginTop: '5px' }}>Error al cargar</span>
                          <span style={{ fontSize: '10px', color: '#999', marginTop: '5px' }}>URL inválida o CORS</span>
                        </div>
                        <div style={{ position: 'absolute', top: '5px', right: '5px', background: 'rgba(0,0,0,0.7)', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>
                          #{index + 1}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Botones */}
          <div className='form-actions'>
            <button type='button' onClick={() => navigate('/mis-vehiculos')} className='cancel-btn' disabled={saving}>
              Cancelar
            </button>
            <button type='submit' className='submit-btn' disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditVehicle
